import { Box, Checkbox, FormControlLabel, Grid, TextField, Theme, useTheme } from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import React, { ReactNode } from 'react';
import { FeedbackSnackbarContext } from '../../context/FeedbackSnackbarContext';
import { Moment } from 'moment';
import moment from 'moment';
import { DocumentReference } from 'firebase/firestore';
import { Region } from '@esg/esg-global-types';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { hasBannedCharacters } from '../../lib/validation/text_validation';
import { MetadataError } from '@ep/error-handling';
import { generateUuidV4 } from '../../util/uuid';
import { log } from '../../util/log';
import GhostButton from '../shared/button/GhostButton';
import PrimaryButton from '../shared/button/PrimaryButton';
import { Subheading } from '../shared/typography/PrimaryTypography';

type InputValueByType = {
  text: string;
  select: string | number;
  date: Moment;
  month_year: Moment;
  node: unknown;
  number: number;
  boolean: boolean;
};

export interface EditInput<T extends keyof InputValueByType = keyof InputValueByType> {
  id: string;
  type: 'text' | 'select' | 'date' | 'month_year' | 'node' | 'number' | 'boolean';
  label: string;
  defaultValue?: string | number | Moment | DocumentReference | boolean | Region;
  optional?: boolean;
  ignoreBannedCharacters?: boolean;
  hideInput?: boolean;
  helperText?: string;
  inputNodeFactory?: (handleInputChange: (input_id: string, value: unknown) => void) => ReactNode;
  textValidation?: (input_value: InputValueByType[T]) => boolean;
}

/**
 * Generic widget for filling in dynamic inputs required for editing an entity in the database
 * @param {string} widget_label Title of the widget shown at the top
 * @param {ReactNode} icon Icon to display next to title
 * @param {Array<EditInput>} inputs List of input fields to fill in for the editing function to use
 * @param {any} edit_entity Entity being editing in the widget
 * @param {number} grid_spacing Optional spacing to add between each input field component
 * @param {void} handleClose Close the widget
 * @param {void} handleEditInput Update the entity being edited by the change functions of the inputs
 * @param {void} confirmEditFunction Use the input field data to edit the entity in the database
 * @returns {JSX.Element}
 */
const ConfigEditWidget = ({
  widget_label,
  icon,
  inputs,
  edit_entity,
  grid_spacing,
  handleClose,
  handleEditInput,
  confirmEditFunction
}: {
  widget_label: string;
  icon: ReactNode;
  inputs: Array<EditInput>;
  edit_entity: any;
  grid_spacing?: number;
  handleClose: () => void;
  handleEditInput: (input_id: string, value: any) => void;
  confirmEditFunction: ((...args: any) => void) | null;
}) => {
  const [editLoading, setEditLoading] = React.useState<boolean>(false);
  const [originalData] = React.useState(edit_entity);
  const { setFeedbackData } = React.useContext(FeedbackSnackbarContext);
  const theme: Theme = useTheme();

  // Checks if every non-optional input has a value (at least 1 option for array inputs), and no banned characters for text inputs.
  // TODO: Follow DRY by unifying ConfigEditWidget and ConfigAddWidget.
  const hasValidInputs = (): boolean => {
    if (edit_entity) {
      const inputs_valid: boolean = inputs.every((input: EditInput) => {
        const input_value: unknown = edit_entity[input.id];

        const is_required_valid: boolean =
          input.optional === true ||
          (input_value !== undefined &&
            input_value !== null &&
            input_value !== '' &&
            (!Array.isArray(input_value) || input_value.length > 0));

        const has_no_banned_characters: boolean =
          input.ignoreBannedCharacters ||
          typeof input_value !== 'string' ||
          !hasBannedCharacters(input_value);

        const passes_custom_validation: boolean =
          typeof input.textValidation !== 'function' || input.textValidation(input_value);

        return is_required_valid && has_no_banned_characters && passes_custom_validation;
      });

      return inputs_valid;
    }
    return false;
  };

  const handleEditClick = async () => {
    setEditLoading(true);
    try {
      if (confirmEditFunction) {
        await confirmEditFunction(edit_entity, originalData);
      }
      handleClose();
    } catch (err: unknown) {
      if (err instanceof Error && err.message === 'invalid edit input') {
        return;
      }
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: ConfigEditWidget failed on an unknown error while calling handleEditClick.',
          {
            edit_entity: edit_entity
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to update entity. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    } finally {
      setEditLoading(false);
    }
    return;
  };
  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Box sx={{ p: 2 }}>
        <Grid container spacing={grid_spacing ? grid_spacing : 6}>
          <Grid size={10}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                color: theme.palette.primary.main
              }}
            >
              {icon}
              <Subheading weight="Medium" sx={{ mx: 2 }}>
                Edit {widget_label}
              </Subheading>
            </Box>
          </Grid>
          {inputs
            .filter((input: EditInput) => !input.hideInput)
            .map((input: EditInput) => {
              return (
                <Grid size={12} key={input.id}>
                  {input.type === 'text' && (
                    <TextField
                      error={
                        !input.ignoreBannedCharacters &&
                        typeof edit_entity[input.id] === 'string' &&
                        edit_entity[input.id] !== '' &&
                        hasBannedCharacters(edit_entity[input.id])
                      }
                      fullWidth
                      id={input.id}
                      label={input.label}
                      defaultValue={
                        input.defaultValue
                          ? input.defaultValue
                          : edit_entity
                            ? edit_entity[input.id]
                            : ''
                      }
                      variant="standard"
                      helperText={input.helperText}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
                        handleEditInput(input.id, event.target.value);
                      }}
                    />
                  )}
                  {input.type === 'number' && (
                    <TextField
                      type="number"
                      fullWidth
                      id={input.id}
                      label={input.label}
                      defaultValue={
                        input.defaultValue
                          ? input.defaultValue
                          : edit_entity
                            ? edit_entity[input.id]
                            : ''
                      }
                      helperText={input.helperText}
                      variant="standard"
                      onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
                        handleEditInput(input.id, event.target.value);
                      }}
                    />
                  )}
                  {input.type === 'date' && (
                    <DatePicker
                      label={input.label}
                      defaultValue={
                        input.defaultValue
                          ? input.defaultValue
                          : edit_entity
                            ? moment(edit_entity[input.id])
                            : null
                      }
                      onChange={(new_date) => handleEditInput(input.id, new_date)}
                      sx={{ width: '100%' }}
                    />
                  )}
                  {input.type === 'month_year' && (
                    <DatePicker
                      views={['year', 'month']}
                      label={input.label}
                      defaultValue={
                        input.defaultValue
                          ? input.defaultValue
                          : edit_entity
                            ? moment(edit_entity[input.id])
                            : null
                      }
                      onChange={(new_date) => handleEditInput(input.id, new_date)}
                      sx={{ width: '100%' }}
                    />
                  )}
                  {input.type === 'boolean' && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={edit_entity[input.id] ? true : false}
                          value={edit_entity[input.id] ? true : false}
                          color="primary"
                          onChange={(event) => {
                            handleEditInput(input.id, event.target.checked);
                          }}
                        />
                      }
                      label={input.label}
                    />
                  )}
                  {input.type === 'node' &&
                    input.inputNodeFactory &&
                    input.inputNodeFactory(handleEditInput)}
                </Grid>
              );
            })}

          <Grid size={12} alignItems={'right'}>
            <Box m={1} display="flex" justifyContent="flex-end" alignItems="flex-end">
              <GhostButton
                startIcon={<ChevronLeftIcon />}
                dark
                onClick={() => {
                  handleClose();
                }}
              >
                Back
              </GhostButton>
              <PrimaryButton
                disabled={!hasValidInputs()}
                loading={editLoading}
                onClick={async () => {
                  await handleEditClick();
                }}
              >
                Update
              </PrimaryButton>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </LocalizationProvider>
  );
};

export default ConfigEditWidget;
